import { useId, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Button,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  CircleCheckIcon,
  CopyIcon,
  LazyFloating,
  Tooltip,
  cn,
  showToast,
} from '@quickmd-llc/design-system'
import { getGetPharmacyOrdersQueryKey } from 'api/generated/pharmacy-order/pharmacy-order'
import { usePutPatientsPatientIdPharmacyOrdersOrderIdShippingAddress } from 'api/generated/pharmacy-fulfillment/pharmacy-fulfillment'
import type {
  CreateShippingLabelDoctorApiOutContract,
  PharmacyOrderCollectionItemDoctorApiOutContract,
  ShipmentTrackingEventDoctorApiOutContract,
} from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { OutShipmentStatus } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { SHIPMENT_STATUS_ORDER, SHIPMENT_STATUS_META, type SortKey, type SortState } from '../constants'
import { formatCarrier, formatCost, formatDate, formatDateTime, formatText, hasActiveLabel } from '../utils'
import { AddressForm } from '../fulfillment/AddressForm'
import { CreateScriptlyOrderButton } from '../fulfillment/CreateScriptlyOrderButton'
import { formatRetryStatus, useFulfillmentAttempt } from '../fulfillment/fulfillmentAttempts'
import { getFulfillmentErrorMessage } from '../fulfillment/fulfillmentErrors'
import type { ShipmentFulfillmentModalProps } from '../fulfillment/ShipmentFulfillmentModal'
import type { ReprintLabelModalProps } from '../fulfillment/ReprintLabelModal'
import type { VoidLabelConfirmModalProps } from '../fulfillment/VoidLabelConfirmModal'
import { useLabelActions } from '../fulfillment/useLabelActions'
import { StatusBadge } from './StatusBadge'
import { NEW_PATIENT_LABEL, NewPatientIcon } from './NewPatientIcon'
import { ColumnFilterMenu, type FilterOption } from './ColumnFilterMenu'

function canFulfill(order: PharmacyOrderCollectionItemDoctorApiOutContract): boolean {
  return !!order.OrderId
}

function canActOnLabel(order: PharmacyOrderCollectionItemDoctorApiOutContract): boolean {
  return (
    !!order.LabelThirdPartyKey &&
    order.ShipmentStatus !== OutShipmentStatus.delivered &&
    order.ShipmentStatus !== OutShipmentStatus.voided
  )
}

// Columns (id, width %) in header order. The Scriptly column sits between PV2 and Shipment and only
// exists when the page is filtered to orders not yet sent to Scriptly. Patient carries the new
// patient icon slot, so it borrows from the short RX ID / Order values and from PV1.
const COLUMNS = [
  { id: 'mrn', width: 11 },
  { id: 'patient', width: 13 },
  { id: 'prescriber', width: 11 },
  { id: 'rx', width: 7 },
  { id: 'rxCreated', width: 8 },
  { id: 'order', width: 7.5 },
  { id: 'orderCreated', width: 12 },
  { id: 'pv1', width: 10.5 },
  { id: 'pv2', width: 11 },
  { id: 'shipment', width: 9 },
]
const COLUMNS_WITH_SCRIPTLY = [
  { id: 'mrn', width: 10 },
  { id: 'patient', width: 12 },
  { id: 'prescriber', width: 9 },
  { id: 'rx', width: 6 },
  { id: 'rxCreated', width: 7 },
  { id: 'order', width: 6.5 },
  { id: 'orderCreated', width: 11 },
  { id: 'pv1', width: 8.5 },
  { id: 'pv2', width: 9 },
  { id: 'scriptly', width: 12 },
  { id: 'shipment', width: 9 },
]

type Props = {
  items: PharmacyOrderCollectionItemDoctorApiOutContract[]
  sort: SortState
  onSort: (key: SortKey) => void
  statusFilter: string[]
  onStatusFilterChange: (next: string[]) => void
  showScriptlyColumn: boolean
}

const TH_CLASS =
  'sticky top-0 z-10 border-b border-border-medium bg-neutral-5 px-sp-2 py-sp-1 text-left align-middle text-xs font-semibold uppercase tracking-wide text-text-light'
const TD_CLASS = 'px-sp-2 py-sp-1 align-top text-sm text-text-dark'
const DETAIL_LABEL_CLASS = 'text-xs font-semibold uppercase tracking-wide text-text-light'

const STATUS_FILTER_OPTIONS: FilterOption[] = SHIPMENT_STATUS_ORDER.map((status) => ({
  value: status,
  label: SHIPMENT_STATUS_META[status].label,
}))

function stop(event: { stopPropagation: () => void }) {
  event.stopPropagation()
}

function copyTracking(trackingNumber: string) {
  navigator.clipboard
    ?.writeText(trackingNumber)
    .then(() => showToast({ type: 'success', message: `Tracking number copied · ${trackingNumber}` }))
    .catch(() => showToast({ type: 'error', message: 'Could not copy tracking number.' }))
}

function SortHeader({
  label,
  columnKey,
  sort,
  onSort,
  align = 'left',
}: {
  label: string
  columnKey: SortKey
  sort: SortState
  onSort: (key: SortKey) => void
  align?: 'left' | 'right'
}) {
  const isActive = sort.key === columnKey
  return (
    <th
      className={cn(TH_CLASS, align === 'right' && 'text-right')}
      aria-sort={
        isActive ?
          sort.direction === 'asc' ?
            'ascending'
          : 'descending'
        : 'none'
      }>
      <button
        type='button'
        className={cn(
          'inline-flex items-center gap-sp-0.5 uppercase hover:text-text-dark',
          align === 'right' && 'flex-row-reverse',
        )}
        onClick={() => onSort(columnKey)}>
        {label}
        {isActive ?
          sort.direction === 'asc' ?
            <ChevronUpIcon iconSize='xs' />
          : <ChevronDownIcon iconSize='xs' />
        : null}
      </button>
    </th>
  )
}

function TrackingCell({ tracking }: { tracking: PharmacyOrderCollectionItemDoctorApiOutContract['Tracking'] }) {
  const hasLink = !!(tracking?.TrackingUrl && tracking.Carrier)
  if (!hasLink) return <>{formatCarrier(tracking?.Carrier)}</>

  return (
    <span className='inline-flex items-center gap-sp-0.5'>
      <a
        className='text-text-highlight underline-offset-2 hover:underline'
        href={tracking.TrackingUrl ?? undefined}
        target='_blank'
        rel='noopener noreferrer'
        onClick={stop}>
        {formatCarrier(tracking.Carrier)}
      </a>
      {tracking?.TrackingNumber ?
        <button
          type='button'
          aria-label='Copy tracking number'
          className='hover:bg-shade-05 flex h-6 w-6 items-center justify-center rounded-md text-text-light hover:text-text-dark'
          onClick={(e) => {
            stop(e)
            copyTracking(tracking.TrackingNumber ?? '')
          }}>
          <CopyIcon iconSize='xs' />
        </button>
      : null}
    </span>
  )
}

// One prescription-verification pass (PV1 or PV2): when it was verified. The
// date is Snowflake-sourced and may be absent (Snowflake degrades gracefully),
// so an unfilled pass renders as an em dash.
function VerificationCell({ date }: { date: string | null | undefined }) {
  if (!date) return <span className='text-text-light'>—</span>
  return <span className='text-text-dark'>{formatDateTime(date)}</span>
}

// Whether the Scriptly order exists for this row. Precedence: no QuickMD order
// yet -> dash; already submitted -> "Created" badge (the backend never marks a
// submitted order triggerable, so it wins only over stale attempt state); a retry is scheduled -> its
// status, no action (the backend accepts a trigger during a scheduled retry and
// its per-order lock runs attempts one at a time, so this client-side retrying
// state, set from MQTT, is what hides the button while a retry is pending);
// pharmacist may create it now -> the action, with the last final failure's
// message under it; anything else -> "Not created". The button sits in a click-stopping wrapper
// because the row itself toggles expansion on click.
function ScriptlyCell({ order }: { order: PharmacyOrderCollectionItemDoctorApiOutContract }) {
  const attempt = useFulfillmentAttempt(order.OrderId ?? null)
  const failureId = useId()

  if (!order.OrderId) return <span className='text-text-light'>—</span>

  if (order.IsSubmittedToPharmacy) {
    return (
      <Badge variant='success' size='s' icon={<CircleCheckIcon iconSize='s' />}>
        Created
      </Badge>
    )
  }

  if (attempt?.kind === 'retrying') {
    return (
      <span role='status' className='text-xs text-text-light'>
        {formatRetryStatus(attempt, ' · ')}
      </span>
    )
  }

  if (order.CanTriggerFulfillment && order.PatientId) {
    const failureMessage = attempt?.kind === 'failed' ? attempt.message : null
    return (
      <span role='presentation' className='inline-flex flex-col items-start gap-sp-0.5' onClick={stop}>
        <CreateScriptlyOrderButton
          patientId={order.PatientId}
          orderId={order.OrderId}
          label='Create order'
          describedBy={failureMessage ? failureId : undefined}
        />
        {failureMessage ?
          <span id={failureId} role='status' className='text-xs text-text-error'>
            {failureMessage}
          </span>
        : null}
      </span>
    )
  }

  return <span className='text-text-light'>Not created</span>
}

function ShipmentHistory({ events }: { events: ShipmentTrackingEventDoctorApiOutContract[] | null | undefined }) {
  if (!events || events.length === 0) return <div className='text-sm text-text-light'>—</div>
  return (
    <ol className='grid gap-sp-1'>
      {events.map((event, index) => (
        <li key={`${event.EventCode}-${event.EventDateTimeUtc}-${index}`} className='text-sm'>
          <div className='text-text-dark'>{formatText(event.Status)}</div>
          {event.EventDescription ?
            <div className='text-text-light'>{event.EventDescription}</div>
          : null}
          <div className='text-xs text-text-light'>{formatDateTime(event.EventDateTimeUtc)}</div>
        </li>
      ))}
    </ol>
  )
}

function LabelActions({
  order,
  onReprintSuccess,
  onVoidClick,
}: {
  order: PharmacyOrderCollectionItemDoctorApiOutContract
  onReprintSuccess: (
    label: CreateShippingLabelDoctorApiOutContract,
    order: PharmacyOrderCollectionItemDoctorApiOutContract,
  ) => void
  onVoidClick: () => void
}) {
  const { reprint, isReprinting } = useLabelActions(
    order.PatientId ?? '',
    order.OrderId ?? '',
    order.LabelThirdPartyKey ?? '',
    { onReprintSuccess: (label) => onReprintSuccess(label, order) },
  )

  return (
    <>
      <Button priority='secondary' size='s' loading={isReprinting} onClick={reprint}>
        Reprint label
      </Button>
      <Button priority='secondary' size='s' onClick={onVoidClick}>
        Void label
      </Button>
    </>
  )
}

function OrderDetail({
  order,
  onCreateLabel,
  onReprintSuccess,
  onVoidClick,
}: {
  order: PharmacyOrderCollectionItemDoctorApiOutContract
  onCreateLabel: (order: PharmacyOrderCollectionItemDoctorApiOutContract) => void
  onReprintSuccess: (
    label: CreateShippingLabelDoctorApiOutContract,
    order: PharmacyOrderCollectionItemDoctorApiOutContract,
  ) => void
  onVoidClick: (order: PharmacyOrderCollectionItemDoctorApiOutContract) => void
}) {
  const [editingAddress, setEditingAddress] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const addressMutation = usePutPatientsPatientIdPharmacyOrdersOrderIdShippingAddress()
  const queryClient = useQueryClient()

  const locked = hasActiveLabel(order)

  return (
    <div className='flex flex-wrap justify-between gap-x-sp-8 gap-y-sp-3 px-sp-6 py-sp-3'>
      <div className='grid content-start gap-sp-1'>
        <div className='flex items-center gap-sp-1'>
          <div className={DETAIL_LABEL_CLASS}>Delivery address</div>
          {!editingAddress && order.OrderId != null ?
            locked ?
              <Tooltip content='Void the shipping label to edit this address.'>
                <Button priority='tertiary' size='s' disabled>
                  Edit
                </Button>
              </Tooltip>
            : <Button
                priority='tertiary'
                size='s'
                onClick={() => {
                  addressMutation.reset()
                  setEditingAddress(true)
                }}>
                Edit
              </Button>

          : null}
        </div>
        {editingAddress ?
          <AddressForm
            initial={order.ShippingAddress}
            pending={addressMutation.isPending || isRefreshing}
            error={
              addressMutation.isError ?
                getFulfillmentErrorMessage(addressMutation.error, 'Could not update the shipping address.')
              : null
            }
            onSubmit={(override) => {
              if (!order.OrderId) return
              addressMutation.mutate(
                { patientId: order.PatientId, orderId: order.OrderId, data: override },
                {
                  onSuccess: () => {
                    setIsRefreshing(true)
                    showToast({ type: 'success', message: 'Shipping address updated.' })
                    void queryClient.invalidateQueries({ queryKey: getGetPharmacyOrdersQueryKey() }).then(() => {
                      setEditingAddress(false)
                      setIsRefreshing(false)
                    })
                  },
                },
              )
            }}
            onCancel={() => setEditingAddress(false)}
          />
        : <>
            <div className='text-sm text-text-dark'>{formatText(order.ShippingAddress?.FormattedAddress)}</div>
            {order.ShippingAddress?.DeliveryInstructions ?
              <div className='mt-sp-0.5'>
                <div className={DETAIL_LABEL_CLASS}>Delivery instructions</div>
                <div className='text-sm text-text-dark'>{order.ShippingAddress.DeliveryInstructions}</div>
              </div>
            : null}
          </>
        }
      </div>

      <div className='grid content-start gap-sp-1'>
        <div className={DETAIL_LABEL_CLASS}>Prescription</div>
        <div className='text-sm text-text-dark'>{formatText(order.Prescription)}</div>
      </div>

      <div className='grid content-start gap-sp-1'>
        <div className={DETAIL_LABEL_CLASS}>Delivery signature</div>
        {/* Set by the backend (insurance-billed, or a controlled substance to a signature state); the
            label wizard pre-checks it and the pharmacist can still opt out. */}
        {order.SignatureRequired ?
          <div className='flex items-center gap-sp-0.5 text-sm text-text-dark'>
            <CheckIcon iconSize='s' className='text-primary-emerald' />
            Required
          </div>
        : <div className='text-sm text-text-dark'>Not required</div>}
      </div>

      <div className='grid content-start gap-sp-1'>
        <div className={DETAIL_LABEL_CLASS}>Tracking</div>
        <div className='text-sm text-text-dark'>
          <TrackingCell tracking={order.Tracking} />
        </div>
        {order.Tracking?.TrackingNumber ?
          <div className='font-mono text-xs text-text-light'>{order.Tracking.TrackingNumber}</div>
        : null}
      </div>

      <div className='grid content-start gap-sp-1'>
        <div className={DETAIL_LABEL_CLASS}>Cost</div>
        <div className='font-mono text-sm tabular-nums text-text-dark'>
          {formatCost(order.LabelThirdPartyKey != null ? order.ShippingCost : null)}
        </div>
      </div>

      <div className='grid content-start gap-sp-1'>
        <div className={DETAIL_LABEL_CLASS}>Shipment history</div>
        <ShipmentHistory events={order.Tracking?.Events} />
        <div className='mt-sp-1 flex flex-wrap gap-sp-1'>
          {canFulfill(order) ?
            <Button priority='primary' size='s' onClick={() => onCreateLabel(order)}>
              Create shipping label
            </Button>
          : null}
          {canActOnLabel(order) ?
            <LabelActions order={order} onReprintSuccess={onReprintSuccess} onVoidClick={() => onVoidClick(order)} />
          : null}
        </div>
      </div>
    </div>
  )
}

function OrderRow({
  order,
  showScriptlyColumn,
  columnCount,
  expanded,
  onToggle,
  onCreateLabel,
  onReprintSuccess,
  onVoidClick,
}: {
  order: PharmacyOrderCollectionItemDoctorApiOutContract
  showScriptlyColumn: boolean
  columnCount: number
  expanded: boolean
  onToggle: () => void
  onCreateLabel: (order: PharmacyOrderCollectionItemDoctorApiOutContract) => void
  onReprintSuccess: (
    label: CreateShippingLabelDoctorApiOutContract,
    order: PharmacyOrderCollectionItemDoctorApiOutContract,
  ) => void
  onVoidClick: (order: PharmacyOrderCollectionItemDoctorApiOutContract) => void
}) {
  return (
    <>
      <tr
        className={cn(
          'hover:bg-coastal-blue-05 cursor-pointer border-b border-border-light',
          expanded && 'bg-coastal-blue-05',
        )}
        onClick={onToggle}>
        <td className={cn(TD_CLASS, 'font-mono', expanded && 'border-l-4 border-coastal-blue-50')}>
          <span className='inline-flex items-center gap-sp-0.5'>
            <button
              type='button'
              aria-label={expanded ? 'Collapse order' : 'Expand order'}
              aria-expanded={expanded}
              className='flex h-5 w-5 items-center justify-center rounded text-text-light hover:text-text-dark'
              onClick={(e) => {
                stop(e)
                onToggle()
              }}>
              {expanded ?
                <ChevronDownIcon iconSize='xs' />
              : <ChevronRightIcon iconSize='xs' />}
            </button>
            {formatText(order.PatientMrn)}
          </span>
        </td>
        <td className={TD_CLASS}>
          {/* The icon sits in a fixed slot before the name (empty on unflagged rows), so icons line up
              down the column and names stay aligned on every row, like the MRN cell's chevron. */}
          <span className='inline-flex items-start gap-sp-0.5'>
            <span className='flex h-5 w-4 shrink-0 items-center justify-center'>
              {order.IsNewToMatHomeDelivery ?
                <Tooltip content={NEW_PATIENT_LABEL}>
                  <NewPatientIcon />
                </Tooltip>
              : null}
            </span>
            {formatText(order.PatientName)}
          </span>
        </td>
        <td className={TD_CLASS}>{formatText(order.PrescriberName)}</td>
        <td className={TD_CLASS}>{formatText(order.Prescription)}</td>
        <td className={TD_CLASS}>{formatDate(order.RxCreatedDate)}</td>
        <td className={cn(TD_CLASS, 'font-mono')}>{formatText(order.OrderNumber)}</td>
        <td className={TD_CLASS}>{formatDateTime(order.OrderCreatedDate)}</td>
        <td className={TD_CLASS}>
          <VerificationCell date={order.Pv1Date} />
        </td>
        <td className={TD_CLASS}>
          <VerificationCell date={order.Pv2Date} />
        </td>
        {showScriptlyColumn ?
          <td className={TD_CLASS}>
            <ScriptlyCell order={order} />
          </td>
        : null}
        <td className={TD_CLASS}>
          <StatusBadge status={order.ShipmentStatus} />
        </td>
      </tr>
      {expanded ?
        <tr className='border-b border-border-light bg-coastal-blue-10'>
          <td colSpan={columnCount} className='border-l-4 border-coastal-blue-50 p-0'>
            <OrderDetail
              order={order}
              onCreateLabel={onCreateLabel}
              onReprintSuccess={onReprintSuccess}
              onVoidClick={onVoidClick}
            />
          </td>
        </tr>
      : null}
    </>
  )
}

export function OrdersTable({ items, sort, onSort, statusFilter, onStatusFilterChange, showScriptlyColumn }: Props) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [fulfillOrder, setFulfillOrder] = useState<PharmacyOrderCollectionItemDoctorApiOutContract | null>(null)
  const [voidOrder, setVoidOrder] = useState<PharmacyOrderCollectionItemDoctorApiOutContract | null>(null)
  const [reprintData, setReprintData] = useState<{
    label: CreateShippingLabelDoctorApiOutContract
    order: PharmacyOrderCollectionItemDoctorApiOutContract
  } | null>(null)
  const queryClient = useQueryClient()
  const columns = showScriptlyColumn ? COLUMNS_WITH_SCRIPTLY : COLUMNS

  const { voidLabel, isVoiding } = useLabelActions(
    voidOrder?.PatientId ?? '',
    voidOrder?.OrderId ?? '',
    voidOrder?.LabelThirdPartyKey ?? '',
    { onVoidSettled: () => setVoidOrder(null) },
  )

  const { confirmShipment, isConfirmingShipment } = useLabelActions(
    reprintData?.order.PatientId ?? '',
    reprintData?.order.OrderId ?? '',
    reprintData?.order.LabelThirdPartyKey ?? '',
    { onConfirmSettled: () => setReprintData(null) },
  )

  return (
    <>
      <table
        className={cn('w-full table-fixed border-collapse', showScriptlyColumn ? 'min-w-[1400px]' : 'min-w-[1200px]')}>
        <colgroup>
          {columns.map((column) => (
            <col key={column.id} style={{ width: `${column.width}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr className='border-b border-border-medium bg-neutral-5'>
            <SortHeader label='Patient MRN' columnKey='mrn' sort={sort} onSort={onSort} />
            <th className={TH_CLASS}>Patient</th>
            <SortHeader label='Prescriber' columnKey='prescriber' sort={sort} onSort={onSort} />
            <th className={TH_CLASS}>RX ID</th>
            <SortHeader label='Rx created' columnKey='rxCreated' sort={sort} onSort={onSort} />
            <th className={TH_CLASS}>Order</th>
            <SortHeader label='Order created' columnKey='orderCreated' sort={sort} onSort={onSort} />
            <th className={TH_CLASS}>PV1</th>
            <th className={TH_CLASS}>PV2</th>
            {showScriptlyColumn ?
              <th className={TH_CLASS}>Scriptly</th>
            : null}
            <th className={TH_CLASS}>
              <span className='inline-flex items-center gap-sp-0.5'>
                Shipment
                <ColumnFilterMenu
                  title='Shipment status'
                  options={STATUS_FILTER_OPTIONS}
                  selected={statusFilter}
                  onChange={onStatusFilterChange}
                />
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((order, index) => {
            const key = `${index}-${order.OrderNumber ?? order.PatientMrn ?? ''}`
            return (
              <OrderRow
                key={key}
                order={order}
                showScriptlyColumn={showScriptlyColumn}
                columnCount={columns.length}
                expanded={expandedKey === key}
                onToggle={() => setExpandedKey((prev) => (prev === key ? null : key))}
                onCreateLabel={(clickedOrder) => setFulfillOrder(clickedOrder)}
                onReprintSuccess={(label, reprintOrder) => setReprintData({ label, order: reprintOrder })}
                onVoidClick={(clickedOrder) => setVoidOrder(clickedOrder)}
              />
            )
          })}
        </tbody>
      </table>

      <LazyFloating<ShipmentFulfillmentModalProps>
        loader={() =>
          import('../fulfillment/ShipmentFulfillmentModal').then((m) => ({
            default: m.ShipmentFulfillmentModal,
          }))
        }
        isOpen={!!fulfillOrder}
        onClose={() => setFulfillOrder(null)}
        patientId={fulfillOrder?.PatientId ?? ''}
        orderId={fulfillOrder?.OrderId ?? ''}
        orderItemId={fulfillOrder?.OrderItemId ?? null}
        shippingAddress={fulfillOrder?.ShippingAddress ?? null}
        hasActiveLabel={fulfillOrder ? hasActiveLabel(fulfillOrder) : false}
        onShipped={() => {
          void queryClient.invalidateQueries({ queryKey: getGetPharmacyOrdersQueryKey() })
        }}
      />

      <LazyFloating<VoidLabelConfirmModalProps>
        loader={() =>
          import('../fulfillment/VoidLabelConfirmModal').then((m) => ({
            default: m.VoidLabelConfirmModal,
          }))
        }
        isOpen={!!voidOrder}
        onClose={() => setVoidOrder(null)}
        onConfirm={() => {
          voidLabel()
        }}
        isVoiding={isVoiding}
        orderNumber={voidOrder?.OrderNumber ?? null}
        trackingNumber={voidOrder?.Tracking?.TrackingNumber ?? null}
      />

      <LazyFloating<ReprintLabelModalProps>
        key={reprintData?.label.LabelThirdPartyKey ?? 'reprint'}
        loader={() =>
          import('../fulfillment/ReprintLabelModal').then((m) => ({
            default: m.ReprintLabelModal,
          }))
        }
        isOpen={!!reprintData}
        onClose={() => setReprintData(null)}
        label={reprintData?.label ?? null}
        shipmentStatus={reprintData?.order.ShipmentStatus ?? null}
        onConfirmShipment={confirmShipment}
        isConfirming={isConfirmingShipment}
      />
    </>
  )
}
